import React from 'react';
import { TrailerType } from '../types';
import { XCircle } from 'lucide-react';

interface TrailerIconProps {
  type: TrailerType | null;
  size?: number;
  className?: string;
}

const TrailerIcon: React.FC<TrailerIconProps> = ({ 
  type, 
  size = 24,
  className = ""
}) => {
  const iconWrapperStyle = {
    backgroundColor: '#f9fafb', // Light gray background matching vehicle icons
    border: '1px solid #e5e7eb', // Light border
    borderRadius: '0.5rem', // 8px rounded corners
    padding: '0.75rem', // 12px padding for more space
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // Subtle shadow for depth
    position: 'relative' as const // For pseudo-element positioning
  };

  // Use currentColor to respect the className color passed in
  const iconStyle = {
    filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.1))' // Subtle shadow on the icon itself
  };

  if (!type) {
    return (
      <div style={iconWrapperStyle}>
        <XCircle size={size} className={className} style={iconStyle} />
      </div>
    );
  }

  switch (type) {
    case TrailerType.DRY_BOX:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size} 
            viewBox="0 0 300 153"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="translate(0.000000,153.000000) scale(0.100000,-0.100000)">
              <path d="M10 925 l0 -585 1480 -1 c814 -1 1486 -2 1493 -3 9 -1 13 124 15 587 l2 587 -1495 0 -1495 0 0 -585z m1163 113 c-2 -5 -3 -157 -3 -338 l0 -331 -152 3 -153 3 -3 338 -2 338 157 -3 c87 -2 157 -6 156 -10z"/>
              <path d="M882 713 l3 -318 135 0 135 0 3 318 2 317 -140 0 -140 0 2 -317z"/>
              <path d="M137 290 c-95 -112 -28 -280 112 -280 140 0 209 168 115 280 -25 29 -28 30 -114 30 -86 0 -89 -1 -113 -30z m180 -5 c37 -24 55 -61 55 -111 0 -57 -23 -91 -76 -118 -32 -16 -44 -17 -75 -8 -87 27 -126 122 -79 192 44 65 115 84 175 45z"/>
              <path d="M203 276 c-81 -37 -82 -160 -2 -202 80 -41 178 44 149 131 -22 67 -86 98 -147 71z"/>
              <path d="M492 285 c-93 -105 -20 -275 117 -275 140 0 209 168 115 280 -25 29 -28 30 -114 30 -88 0 -88 0 -118 -35z m181 3 c55 -29 76 -141 35 -193 -48 -61 -131 -67 -186 -14 -70 69 -45 180 48 218 26 10 72 6 103 -11z"/>
              <path d="M563 276 c-35 -16 -63 -61 -63 -101 0 -95 116 -148 179 -81 84 89 -6 231 -116 182z"/>
              <path d="M2498 198 l-3 -123 -67 -3 c-68 -3 -68 -3 -68 -32 l0 -30 215 0 215 0 0 30 0 29 -77 3 -78 3 -3 123 -3 122 -64 0 -64 0 -3 -122z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.REFRIGERATED_BOX:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size} 
            viewBox="0 0 1065 524"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="matrix(0.195211, 0, 0, -0.205031, 93.08, 1090.89)">
              <path d="M192 4283 l3 -818 108 -3 108 -3 -30 -25 c-100 -83 -97 -268 5 -353 145 -119 348 -14 348 179 0 72 -17 114 -67 168 l-31 32 133 0 133 0 -30 -25 c-99 -83 -97 -270 4 -354 145 -119 348 -14 348 179 0 72 -17 114 -67 168 l-31 32 1227 0 1227 0 0 -170 0 -170 -90 0 -90 0 0 -40 0 -40 290 0 290 0 0 40 0 40 -105 0 -105 0 0 170 0 170 260 0 260 0 0 415 0 415 95 0 95 0 0 380 0 380 -95 0 c-95 0 -95 0 -95 25 l0 25 -2050 0 -2050 0 2 -817z m391 -857 c121 -52 145 -214 44 -303 -85 -74 -210 -45 -258 61 -23 50 -24 95 -4 143 21 50 42 72 90 94 48 22 85 24 128 5z m525 -15 c105 -74 92 -249 -22 -308 -39 -20 -114 -19 -154 1 -82 42 -117 158 -73 243 19 38 54 71 91 86 33 14 126 1 158 -22z"/>
              <path d="M470 3403 c-8 -3 -26 -15 -38 -27 -71 -64 -70 -168 2 -235 37 -34 118 -40 164 -11 35 22 72 88 72 130 0 42 -37 108 -72 130 -32 19 -94 25 -128 13z"/>
              <path d="M960 3409 c-40 -16 -80 -60 -91 -101 -28 -108 62 -213 167 -193 48 9 101 58 114 107 32 118 -81 230 -190 187z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.LIQUID_TANK:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size} 
            viewBox="0 0 1020 490"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="matrix(0.249203, 0, 0, -0.306983, -44.9601, 1449.14)">
              <path d="M431 4695 c-54 -17 -96 -56 -116 -109 -13 -35 -15 -97 -13 -427 l3 -385 27 -41 c16 -24 44 -49 70 -62 l44 -21 1602 0 1602 0 0 -215 0 -215 -100 0 -100 0 0 -45 0 -45 300 0 300 0 0 45 0 45 -100 0 -100 0 0 215 0 215 105 0 c114 0 157 13 202 62 41 44 43 65 43 465 0 372 -1 388 -21 430 -15 30 -35 52 -67 71 l-47 27 -1795 2 c-1496 1 -1802 0 -1839 -12z"/>
              <path d="M1038 3630 c-81 -24 -152 -105 -164 -186 -35 -238 236 -382 387 -205 64 73 77 177 35 266 -28 60 -56 87 -116 114 -47 21 -96 25 -142 11z m140 -69 c46 -23 67 -46 87 -94 48 -115 -27 -245 -149 -255 -158 -13 -249 165 -154 301 44 62 144 85 216 48z"/>
              <path d="M1020 3528 c-65 -45 -88 -132 -54 -204 21 -43 55 -71 106 -85 35 -9 46 -7 86 13 61 30 84 65 89 130 5 65 -17 112 -66 145 -46 31 -116 31 -161 1z"/>
              <path d="M505 3614 c-176 -96 -177 -348 -1 -430 88 -41 175 -27 245 39 106 100 92 288 -28 372 -36 25 -53 30 -114 32 -52 3 -80 -1 -102 -13z m140 -35 c125 -35 172 -203 84 -303 -116 -132 -322 -39 -307 139 6 70 39 118 102 150 56 28 65 29 121 14z"/>
              <path d="M526 3538 c-70 -41 -97 -136 -60 -214 36 -76 138 -107 203 -62 58 39 76 72 76 138 0 44 -5 66 -21 86 -53 71 -130 92 -198 52z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.PLATFORM:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size * 0.7} 
            viewBox="0 0 300 107"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="translate(0.000000,107.000000) scale(0.100000,-0.100000)">
              <path d="M90 425 l0 -45 62 0 62 0 -31 -32 c-42 -45 -53 -70 -53 -118 0 -155 189 -216 265 -85 44 75 30 156 -37 209 l-33 26 110 0 110 0 -31 -25 c-70 -55 -81 -164 -22 -230 80 -92 215 -57 248 65 15 56 -2 111 -47 155 l-36 35 961 0 962 0 0 -120 0 -120 -65 0 c-63 0 -65 -1 -65 -25 l0 -25 195 0 195 0 0 25 c0 24 -2 25 -65 25 l-66 0 3 117 3 118 83 3 82 3 0 44 0 45 -1395 0 -1395 0 0 -45z m246 -91 c96 -64 53 -224 -61 -224 -113 0 -157 158 -63 224 29 21 92 20 124 0z m339 -14 c55 -52 57 -132 3 -182 -24 -22 -40 -28 -75 -28 -38 0 -50 5 -79 34 -29 29 -34 41 -34 81 0 57 12 80 53 106 40 25 99 20 132 -11z"/>
              <path d="M253 332 c-12 -2 -34 -17 -48 -33 -82 -98 46 -229 136 -140 70 70 10 188 -88 173z"/>
              <path d="M542 305 c-39 -32 -48 -72 -28 -119 17 -42 70 -70 111 -60 35 9 75 59 75 95 0 37 -24 85 -49 99 -34 17 -78 11 -109 -15z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.CONTAINER_CARRIER:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size * 0.7} 
            viewBox="0 0 300 128"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="translate(0.000000,128.000000) scale(0.100000,-0.100000)">
              <path d="M2828 768 l-3 -283 -1338 -3 -1337 -2 0 -40 0 -40 68 0 67 0 -33 -20 c-35 -22 -72 -88 -72 -130 0 -42 37 -108 72 -130 46 -29 127 -23 164 11 37 34 54 72 54 119 0 51 -20 91 -62 124 l-33 26 110 0 110 0 -32 -25 c-41 -33 -68 -102 -58 -152 15 -82 76 -128 157 -121 76 8 128 68 128 148 0 51 -20 91 -62 124 l-33 26 953 0 952 0 0 -115 0 -115 -65 0 -65 0 0 -30 0 -30 195 0 195 0 0 30 0 30 -65 0 -65 0 0 115 0 115 80 0 80 0 0 325 0 325 -30 0 -30 0 -2 -282z m-2440 -414 c50 -35 66 -112 35 -165 -18 -30 -67 -59 -100 -59 -49 0 -113 71 -113 126 0 30 32 85 59 99 35 19 91 19 119 -1z m312 1 c42 -22 60 -53 60 -107 0 -39 -6 -54 -29 -80 -26 -29 -36 -33 -79 -33 -40 0 -56 5 -76 24 -57 54 -51 145 13 188 39 27 71 29 111 8z"/>
              <path d="M267 330 c-52 -41 -44 -136 14 -165 43 -23 77 -18 110 14 43 43 38 121 -10 158 -25 19 -85 15 -114 -7z"/>
              <path d="M603 338 c-12 -6 -29 -26 -39 -46 -13 -29 -14 -42 -5 -72 28 -92 147 -94 182 -2 30 79 -60 158 -138 120z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.PRESSURIZED_HOPPER:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size * 0.7} 
            viewBox="0 0 300 122"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="translate(0.000000,122.000000) scale(0.100000,-0.100000)">
              <path d="M790 1155 c0 -24 -2 -25 -67 -25 -50 0 -137 -18 -323 -65 -183 -46 -257 -61 -262 -52 -14 23 -21 -4 -30 -110 -9 -104 -3 -373 9 -421 4 -16 10 -21 23 -17 24 7 529 -119 546 -137 12 -12 9 -13 -20 -10 -41 5 -102 -22 -123 -54 -23 -36 -81 -33 -118 6 -42 43 -96 56 -151 35 -24 -9 -52 -27 -63 -41 -13 -16 -29 -24 -50 -24 -31 0 -31 -1 -31 -50 0 -47 2 -50 25 -50 17 0 25 -6 25 -17 0 -26 33 -72 66 -94 39 -25 109 -25 152 1 20 13 41 38 53 65 25 57 48 58 74 4 26 -55 63 -81 122 -86 61 -6 108 23 138 85 l20 42 522 0 522 0 1 93 c0 50 1 100 1 109 0 15 10 18 65 18 l64 0 0 -37 c0 -24 8 -45 22 -60 l21 -23 -21 0 c-20 0 -22 -5 -22 -50 l0 -50 209 0 c114 0 229 -3 255 -6 l46 -7 0 112 c0 106 1 112 23 121 45 19 426 110 440 104 8 -3 17 2 21 13 13 32 18 346 8 441 -10 95 -17 116 -31 94 -6 -10 -67 2 -269 53 -255 63 -265 65 -392 65 l-130 0 0 25 c0 25 0 25 -100 25 -100 0 -100 0 -100 -25 0 -24 -2 -25 -75 -25 -73 0 -75 1 -75 25 0 25 0 25 -100 25 -100 0 -100 0 -100 -25 l0 -25 -110 0 -110 0 0 25 c0 25 0 25 -100 25 -100 0 -100 0 -100 -25 0 -25 0 -25 -100 -25 -100 0 -100 0 -100 25 0 25 0 25 -100 25 -100 0 -100 0 -100 -25z m1630 -865 l0 -50 -95 0 c-52 0 -95 2 -95 4 0 2 26 25 58 50 45 36 65 46 95 46 l37 0 0 -50z m-1245 -24 l30 -25 -109 -1 c-68 0 -106 4 -100 9 5 5 29 25 54 44 50 39 45 40 125 -27z m403 22 l56 -43 -57 -3 c-31 -2 -83 -2 -117 0 l-60 3 52 42 c29 23 57 42 62 43 5 0 34 -19 64 -42z m-836 -37 c23 -25 28 -41 28 -81 0 -68 -36 -110 -101 -117 -41 -5 -50 -2 -82 27 -62 54 -60 131 6 186 43 37 109 30 149 -15z m-368 14 c67 -32 88 -127 41 -184 -54 -64 -142 -59 -185 11 -36 60 -18 131 43 168 39 24 59 25 101 5z"/>
              <path d="M595 246 c-66 -66 -27 -176 63 -176 90 0 130 109 65 175 -35 34 -94 35 -128 1z"/>
              <path d="M281 247 c-31 -18 -50 -54 -51 -95 0 -40 54 -102 89 -102 30 0 78 26 91 49 18 35 12 98 -12 126 -28 31 -83 41 -117 22z"/>
              <path d="M1870 205 l0 -135 -70 0 -70 0 0 -30 0 -30 195 0 195 0 0 30 0 30 -75 0 -75 0 0 135 0 135 -50 0 -50 0 0 -135z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.GRAIN_CAGE:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size} 
            viewBox="0 0 300 153"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="translate(0.000000,153.000000) scale(0.050000,-0.050000)">
              <path d="M25 1855 l5 -1165 2975 -5 2975 -5 0 1170 0 1170 -2980 0 -2980 0 5 -1165z m2635 -745 l0 -330 -238 0 -238 0 -7 219 c-4 121 -4 269 0 330 l7 111 238 0 238 0 0 -330z m1430 0 l0 -320 -245 -6 -245 -5 0 331 0 331 245 -5 245 -6 0 -320z m-2795 -25 l6 -325 -251 0 -250 0 0 317 c0 377 -24 345 259 339 l231 -6 5 -325z m4225 15 l0 -320 -240 0 -240 0 0 320 0 320 240 0 240 0 0 -320z"/>
              <path d="M2220 1320 l0 -80 200 0 200 0 0 80 0 80 -200 0 -200 0 0 -80z"/>
              <path d="M2415 1185 l-195 -6 0 -180 0 -179 200 0 200 0 0 190 c0 105 -2 188 -5 186 -3 -2 -93 -7 -200 -11z"/>
              <path d="M3640 1310 l0 -90 210 0 210 0 0 90 0 90 -210 0 -210 0 0 -90z"/>
              <path d="M3640 990 l0 -170 210 0 210 0 0 170 0 170 -210 0 -210 0 0 -170z"/>
              <path d="M840 1301 l0 -81 211 0 211 0 -6 75 -6 75 -205 6 -205 5 0 -80z"/>
              <path d="M840 980 l0 -180 210 0 210 0 0 180 0 180 -210 0 -210 0 0 -180z"/>
              <path d="M5080 1310 l0 -90 200 0 200 0 0 90 0 90 -200 0 -200 0 0 -90z"/>
              <path d="M5080 990 l0 -170 200 0 200 0 0 170 0 170 -200 0 -200 0 0 -170z"/>
              <path d="M278 578 c-195 -209 -57 -558 222 -558 279 0 417 349 222 558 -88 94 -356 94 -444 0z m344 -12 c170 -100 148 -382 -36 -459 -261 -109 -463 258 -243 442 72 61 192 68 279 17z"/>
              <path d="M354 504 c-105 -110 -82 -288 47 -354 187 -97 370 115 269 310 -61 117 -224 140 -316 44z"/>
              <path d="M995 585 c-194 -203 -78 -538 195 -561 298 -25 447 332 234 561 -78 83 -350 83 -429 0z m399 -73 c56 -61 66 -86 66 -172 0 -271 -351 -357 -467 -114 -127 269 202 504 401 286z"/>
              <path d="M1079 503 c-102 -103 -102 -223 0 -326 132 -131 340 -31 340 163 0 194 -208 294 -340 163z"/>
              <path d="M4956 395 l-6 -245 -135 -6 c-135 -6 -135 -6 -135 -65 l0 -59 420 0 420 0 0 59 0 59 -145 6 -145 6 -6 245 -5 245 -129 0 -129 0 -5 -245z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.GRAIN_HOPPER:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size * 0.7} 
            viewBox="0 0 984 503"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="matrix(0.218959, 0, 0, -0.223988, -7.13751, 1120.46)">
              <path d="M60 4095 l0 -885 120 0 121 0 -36 -32 c-71 -62 -101 -170 -74 -265 17 -62 97 -147 154 -162 82 -22 153 -7 215 47 112 95 118 264 15 368 l-44 44 144 0 145 0 -45 -41 c-58 -54 -78 -108 -73 -197 5 -88 41 -153 111 -196 155 -96 350 20 350 209 -1 76 -26 138 -78 188 l-38 37 210 0 211 0 33 -135 33 -135 263 0 263 0 7 28 c3 15 18 74 33 132 26 103 27 105 55 105 28 0 29 -2 55 -105 15 -58 30 -117 33 -132 l7 -28 259 0 259 0 33 135 33 136 30 -3 c29 -3 31 -6 56 -108 15 -58 30 -117 33 -133 l7 -27 247 2 247 3 26 105 c15 58 30 117 33 133 l7 27 170 0 170 0 0 -175 0 -175 -105 0 -105 0 0 -45 0 -45 315 0 315 0 0 45 0 45 -105 0 -105 0 0 175 0 175 210 2 211 3 2 882 2 883 -2217 0 -2218 0 0 -885z m4420 -860 c0 -13 -252 -15 -2205 -15 -1953 0 -2205 2 -2205 15 0 13 252 15 2205 15 1953 0 2205 -2 2205 -15z m-4015 -56 c76 -21 135 -107 135 -194 -1 -65 -61 -151 -123 -174 -108 -41 -233 40 -245 158 -8 89 35 167 113 202 47 22 66 23 120 8z m556 -21 c148 -83 120 -315 -43 -359 -35 -9 -52 -8 -95 5 -90 29 -141 111 -130 209 16 131 157 207 268 145z"/>
              <path d="M355 3146 c-156 -72 -109 -316 61 -316 77 0 144 65 152 148 11 121 -110 216 -213 168z"/>
              <path d="M856 3130 c-69 -44 -95 -142 -60 -217 42 -88 153 -117 228 -59 125 95 64 296 -90 296 -32 0 -59 -7 -78 -20z"/>
            </g>
          </svg>
        </div>
      );

    case TrailerType.LOWBOY:
      return (
        <div style={iconWrapperStyle}>
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width={size * 2} 
            height={size * 0.7} 
            viewBox="0 0 300 85"
            fill="currentColor"
            className={className}
            style={iconStyle}
          >
            <g transform="matrix(0.102629, 0, 0, -0.118877, -6.66953, 86.3214)">
              <path d="M70 410 c0 -29 2 -30 46 -30 l46 0 -32 -26 c-60 -51 -77 -125 -42 -194 69 -138 272 -84 272 73 0 47 -20 87 -60 121 l-32 26 109 0 108 0 -34 -27 c-61 -48 -77 -123 -41 -194 27 -51 72 -79 130 -79 137 0 195 165 94 266 l-36 35 43 -3 c42 -3 47 -7 133 -110 l89 -108 681 0 681 0 83 110 83 110 64 0 65 0 0 -125 0 -125 -76 0 -75 0 3 -27 3 -28 193 -3 192 -2 0 30 c0 30 0 30 -55 30 l-55 0 0 125 0 125 165 0 165 0 0 30 0 30 -315 0 c-190 0 -315 -4 -315 -9 0 -6 -34 -55 -77 -110 l-76 -101 -656 1 -656 0 -90 110 -89 109 -318 0 -318 0 0 -30z m220 -89 c53 -52 52 -131 -1 -175 -39 -33 -72 -41 -108 -26 -68 28 -99 106 -67 168 22 43 54 62 104 62 35 0 49 -6 72 -29z m319 3 c62 -52 58 -147 -8 -191 -43 -29 -79 -29 -122 0 -94 63 -51 217 61 217 27 0 48 -8 69 -26z"/>
              <path d="M174 324 c-36 -18 -54 -51 -54 -100 0 -64 70 -109 131 -84 39 16 59 46 59 87 0 47 -19 80 -55 98 -40 18 -42 18 -81 -1z"/>
              <path d="M483 315 c-13 -9 -28 -33 -34 -54 -35 -118 124 -184 177 -73 13 29 14 41 5 73 -6 21 -21 45 -34 54 -28 19 -86 19 -114 0z"/>
            </g>
          </svg>
        </div>
      );

    default:
      return (
        <div style={iconWrapperStyle}>
          <XCircle size={size} className={className} style={iconStyle} />
        </div>
      );
  }
};

export default TrailerIcon;